import { Fragment, useState } from "react";
import { Link } from "react-router-dom";

import { DeltaBar } from "../components/Charts.tsx";
import { DataTable, type Column } from "../components/DataTable.tsx";
import { ItemLabel } from "../components/ItemLabel.tsx";
import { rememberVariantLabel } from "../lib/variants.tsx";
import {
  Badge,
  Card,
  Empty,
  Notice,
  Spinner,
  StatTile,
} from "../components/Ui.tsx";
import { api } from "../lib/api.ts";
import { useAsync } from "../lib/auth.tsx";
import {
  batchPrice,
  money,
  moneyShort,
  number,
  percent,
  relativeTime,
  taxMoney,
  unitMoney,
} from "../lib/format.ts";
import { PLANS, salesTaxExact } from "@dca/shared";

type Row = Awaited<ReturnType<typeof api.insights.myShop>>["rows"][number];
type Filter =
  | "all"
  | "in stock"
  | "empty"
  | "overpriced"
  | "underpriced"
  | "variants"
  | "unlabelled";

type Sort = "active" | "revenue" | "gap" | "stock" | "value" | "name";

const SORTS: Array<{ key: Sort; label: string }> = [
  { key: "active", label: "Most active" },
  { key: "revenue", label: "Revenue" },
  { key: "gap", label: "Price gap" },
  { key: "stock", label: "Lowest stock" },
  { key: "value", label: "Stock value" },
  { key: "name", label: "Name" },
];

const FILTERS: Filter[] = [
  "all",
  "in stock",
  "empty",
  "overpriced",
  "underpriced",
  "variants",
  "unlabelled",
];

/**
 * How busy an item is.
 *
 * Real sales win when a webhook is connected. Without one, the market-wide
 * units-per-day behind the price band is the only activity signal there is -
 * it is not this shop's own sales, but an item the whole server moves
 * thousands of a day is still the one worth looking at first.
 */
function activity(row: Row): number {
  if (row.unitsSold30d > 0) return row.unitsSold30d * 1000;
  return row.band?.unitsPerDay ?? 0;
}

function stockValue(row: Row): number {
  return (row.currentStock ?? 0) * row.yourPrice;
}

function compare(sort: Sort, a: Row, b: Row): number {
  if (sort === "name")
    return a.title.localeCompare(b.title, undefined, { numeric: true });
  if (sort === "stock") return (a.currentStock ?? 0) - (b.currentStock ?? 0);
  if (sort === "value") return stockValue(b) - stockValue(a);
  if (sort === "revenue") return b.revenue30d - a.revenue30d;
  // A missing benchmark is unknown, not zero, so it sorts last either way
  // rather than sitting in the middle of the list pretending to be on-market.
  if (sort === "gap") {
    const left = a.deltaPct == null ? null : Math.abs(a.deltaPct);
    const right = b.deltaPct == null ? null : Math.abs(b.deltaPct);
    if (left == null && right == null) return 0;
    if (left == null) return 1;
    if (right == null) return -1;
    return right - left;
  }
  return activity(b) - activity(a);
}
type View = "tiles" | "table";

/**
 * Every sign the subscriber owns.
 *
 * The headline number is the price on the sign, not the derived per-unit
 * figure: the sign price is what the owner actually set and what a customer
 * sees, and for bulk goods the per-unit value is fractions of a cent.
 */
export function MyShopPage() {
  const shop = useAsync(() => api.insights.myShop(), []);
  const [filter, setFilter] = useState<Filter>("all");
  const [sort, setSort] = useState<Sort>("active");
  const [query, setQuery] = useState("");
  const [shopId, setShopId] = useState<string | "all">("all");
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const refreshItem = async (material: string) => {
    setRefreshing(material);
    setNote(null);
    try {
      const result = await api.insights.refreshItem(material);
      setNote(result.reason);
      // Reload regardless: a refresh that changed nothing still needs the note
      // to appear, and silently doing nothing is what made this look broken.
      shop.reload();
    } finally {
      setRefreshing(null);
    }
  };

  const labelVariant = async (itemKey: string) => {
    const text = window.prompt(
      `What is "${itemKey}"?

Enchantments or a description - "Efficiency 4, Unbreaking 3", "Brown Axolotl". Shorthand like "Sharp4, Knock2" is fine.`,
    );
    if (!text?.trim()) return;
    setNote(null);
    try {
      const result = await api.insights.labelVariant(itemKey, text.trim());
      // Seed the shared cache so the new name shows on every page at once,
      // rather than only after this one refetches.
      rememberVariantLabel(result.label);
      setNote(
        result.outcome === "confirmed"
          ? `Confirmed "${result.label.label}" - ${result.label.confirmations} people agree.`
          : `Labelled as "${result.label.label}". It now shows for everyone who sells it.`,
      );
      shop.reload();
    } catch (err) {
      setNote(err instanceof Error ? err.message : "Could not save that label");
    }
  };

  const hideItem = async (itemKey: string) => {
    setRefreshing(itemKey);
    setNote(null);
    try {
      await api.insights.hideItem(itemKey);
      setNote(
        `Hidden "${itemKey}" from your shop view. It still counts toward the market.`,
      );
      shop.reload();
    } finally {
      setRefreshing(null);
    }
  };

  const unhideAll = async () => {
    const result = await api.insights.unhideAll();
    setNote(
      result.restored > 0
        ? `Restored ${result.restored} hidden signs.`
        : "Nothing was hidden.",
    );
    shop.reload();
  };

  const refreshAll = async () => {
    setRefreshing("__all__");
    setNote(null);
    try {
      const result = await api.insights.refreshShop();
      setNote(
        result.refreshed > 0
          ? `Re-read ${result.refreshed} of ${result.itemsChecked} items from the live market.`
          : "Everything was refreshed moments ago - nothing to re-read.",
      );
      if (result.refreshed > 0) shop.reload();
    } finally {
      setRefreshing(null);
    }
  };
  const [view, setView] = useState<View>(() => {
    try {
      return (localStorage.getItem("dca.myshop.view") as View) ?? "tiles";
    } catch {
      return "tiles";
    }
  });

  const chooseView = (next: View) => {
    setView(next);
    try {
      localStorage.setItem("dca.myshop.view", next);
    } catch {
      /* storage unavailable - the choice just does not persist */
    }
  };

  if (shop.loading) return <Spinner />;
  const data = shop.data;

  if (!data || data.rows.length === 0) {
    return (
      <>
        <header className="page-head">
          <div>
            <h1>My shop</h1>
            <p>Every item you list, with its stock and market comparison.</p>
          </div>
        </header>
        <Empty title="No listings found for your account">
          <p className="muted">
            Claim your shops from public market data and they appear here - no
            API key needed.
          </p>
          <Link to="/connections">
            <button className="primary">Find my shops</button>
          </Link>
        </Empty>
      </>
    );
  }

  const inShop = (row: Row) =>
    shopId === "all" || row.shopGroupIds.includes(shopId);

  // Search across everything a person might type: the item name, the label
  // somebody gave an enchanted variant, the raw key, the material, and the
  // world or coordinates of any sign selling it.
  const needle = query.trim().toLowerCase();
  const matches = (row: Row) => {
    if (!needle) return true;
    const haystack = [
      row.title,
      row.displayName,
      row.itemKey,
      row.material,
      row.variantLabel ?? "",
      row.world ?? "",
      row.coords ?? "",
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(needle);
  };

  const rows = data.rows
    .filter(inShop)
    .filter(matches)
    .filter((row) => {
      if (filter === "in stock") return (row.currentStock ?? 0) > 0;
      if (filter === "empty") return row.currentStock === 0;
      if (filter === "overpriced") return (row.deltaPct ?? 0) > 10;
      if (filter === "underpriced") return (row.deltaPct ?? 0) < -10;
      if (filter === "variants") return row.isVariant;
      if (filter === "unlabelled")
        return row.variantHash != null && row.variantLabel == null;
      return true;
    })
    // Busiest first by default: the item a shop should look at is the one
    // moving, not the one that happens to sort first alphabetically.
    .sort((a, b) => compare(sort, a, b));

  const scoped = data.rows.filter(inShop);
  const priced = scoped.filter((row) => row.deltaPct != null);
  const over = priced.filter((row) => row.deltaPct! > 10).length;
  const under = priced.filter((row) => row.deltaPct! < -10).length;
  const emptyCount = scoped.filter((row) => row.currentStock === 0).length;
  const stockedCount = scoped.length - emptyCount;
  const variantCount = scoped.filter((row) => row.isVariant).length;
  const unlabelledCount = scoped.filter(
    (row) => row.variantHash != null && row.variantLabel == null,
  ).length;

  return (
    <>
      <header className="page-head">
        <div>
          <h1>My shop</h1>
          <p>
            Every sign you run, priced as written. Custom and enchanted items
            are compared only against the same variant - never against the plain
            material.
          </p>
        </div>
        <div className="toolbar">
          <button onClick={refreshAll} disabled={refreshing != null}>
            {refreshing === "__all__" ? "Refreshing..." : "Refresh from market"}
          </button>
          <button
            className="ghost"
            onClick={unhideAll}
            disabled={refreshing != null}
          >
            Restore hidden
          </button>
          <div className="view-toggle">
            <button
              className={view === "tiles" ? "active" : ""}
              onClick={() => chooseView("tiles")}
            >
              Tiles
            </button>
            <button
              className={view === "table" ? "active" : ""}
              onClick={() => chooseView("table")}
            >
              Table
            </button>
          </div>
        </div>
      </header>

      {note && (
        <div style={{ marginBottom: 16 }}>
          <Notice>{note}</Notice>
        </div>
      )}

      {data.medianUpstreamAgeHours != null &&
        data.medianUpstreamAgeHours > 24 && (
          <div style={{ marginBottom: 16 }}>
            <Notice tone="warn">
              <strong>
                The server's own stock readings are about{" "}
                {Math.round(data.medianUpstreamAgeHours / 24)} days old for a
                typical shop.
              </strong>{" "}
              That is upstream staleness, not ours - we last read the market{" "}
              {relativeTime(data.lastFetchedAt)}. Refreshing pulls the newest
              snapshot available, which for most listings will be the same one.
            </Notice>
          </div>
        )}

      {data.shops.length > 1 && (
        <div className="shop-tabs">
          <button
            className={shopId === "all" ? "active" : ""}
            onClick={() => setShopId("all")}
          >
            All shops <span className="count">{data.totalSigns}</span>
          </button>
          {data.shops.map((shop) => (
            <button
              key={shop.id}
              className={shopId === shop.id ? "active" : ""}
              onClick={() => setShopId(shop.id)}
              title={`${shop.world} at ${shop.coords}`}
            >
              {shop.name} <span className="count">{shop.listingCount}</span>
            </button>
          ))}
        </div>
      )}

      {/* Plan limits. The dashboard withholds signs over the allowance rather
          than refusing the page, so this banner is the only place the owner
          learns that anything is missing - it has to be explicit about how
          much and what fixes it. */}
      {!data.quota.withinLimits && (
        <div className="notice warn quota-notice">
          <div>
            <strong>
              {number(data.quota.hiddenSigns)} chestshop
              {data.quota.hiddenSigns === 1 ? "" : "s"} hidden by your plan
            </strong>
            <div className="small">
              {PLANS[data.quota.tier].name} covers{" "}
              {data.quota.maxChestshops == null
                ? "unlimited chestshops"
                : `${number(data.quota.maxChestshops)} chestshops`}{" "}
              at{" "}
              {data.quota.maxLocations == null
                ? "any number of locations"
                : `${data.quota.maxLocations} location${data.quota.maxLocations === 1 ? "" : "s"}`}
              . You have {number(data.quota.chestshopsUsed)} at{" "}
              {data.quota.locationsUsed}.
            </div>
          </div>
          {data.quota.suggestedTier && (
            <Link className="button" to="/billing">
              See {PLANS[data.quota.suggestedTier].name}
            </Link>
          )}
        </div>
      )}

      <div className="grid tiles">
        <StatTile
          label="Items"
          value={number(scoped.length)}
          foot={
            <span className="muted">
              {number(data.totalSigns)} signs in total
            </span>
          }
        />
        <StatTile
          label="Out of stock"
          value={number(emptyCount)}
          foot={<span className="muted">every sign empty</span>}
        />
        <StatTile
          label="Above market"
          value={number(over)}
          foot={<span className="muted">by more than 10%</span>}
        />
        <StatTile
          label="No comparison"
          value={number(data.noMarket)}
          foot={
            <span className="muted">
              {number(variantCount)} custom or enchanted
            </span>
          }
        />
      </div>

      <Card
        title={`${rows.length} item${rows.length === 1 ? "" : "s"}`}
        subtitle={
          needle || filter !== "all"
            ? `filtered from ${number(scoped.length)}`
            : undefined
        }
      >
        {/* Search, sort and filter sit above both views and govern both. The
            table used to carry its own search box, which meant the tile view
            had none and the two could disagree about what was on screen. */}
        <div className="shop-toolbar">
          <input
            type="search"
            className="shop-search"
            value={query}
            placeholder="Search items, labels, materials or locations"
            aria-label="Search your shop"
            onChange={(event) => setQuery(event.target.value)}
          />

          <div className="toolbar-group">
            <span className="toolbar-label">Sort</span>
            <div className="pill-row">
              {SORTS.map((option) => (
                <button
                  key={option.key}
                  className={`pill ${sort === option.key ? "active" : ""}`}
                  onClick={() => setSort(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="toolbar-group">
            <span className="toolbar-label">Filter</span>
            <div className="pill-row">
              {FILTERS.map((option) => (
                <button
                  key={option}
                  className={`pill ${filter === option ? "active" : ""}`}
                  onClick={() => setFilter(option)}
                >
                  {option}
                  {option === "in stock" ? ` (${stockedCount})` : ""}
                  {option === "empty" ? ` (${emptyCount})` : ""}
                  {option === "overpriced" ? ` (${over})` : ""}
                  {option === "underpriced" ? ` (${under})` : ""}
                  {option === "variants" ? ` (${variantCount})` : ""}
                  {option === "unlabelled" ? ` (${unlabelledCount})` : ""}
                </button>
              ))}
            </div>
          </div>

          {(needle || filter !== "all") && (
            <button
              className="ghost"
              onClick={() => {
                setQuery("");
                setFilter("all");
              }}
            >
              Clear
            </button>
          )}
        </div>

        {view === "tiles" ? (
          <TileGrid
            rows={rows}
            onRefresh={refreshItem}
            onHide={hideItem}
            onLabel={labelVariant}
            refreshing={refreshing}
          />
        ) : (
          <DataTable
            rows={rows}
            columns={buildColumns(
              data.hasSalesData,
              refreshItem,
              hideItem,
              labelVariant,
              refreshing,
            )}
            rowKey={(row) => row.itemKey}
            hideSearch
            pageSize={50}
            emptyTitle="Nothing matches that filter"
          />
        )}
      </Card>

      {!data.hasSalesData && (
        <div style={{ marginTop: 16 }}>
          <Notice>
            Units and revenue need an account webhook. Everything else here is
            live from public market data.{" "}
            <Link to="/connections">Connect one</Link>
          </Notice>
        </div>
      )}
    </>
  );
}

function TileGrid({
  rows,
  onRefresh,
  onHide,
  onLabel,
  refreshing,
}: {
  rows: Row[];
  onRefresh: (material: string) => void;
  onHide: (itemKey: string) => void;
  onLabel: (itemKey: string) => void;
  refreshing: string | null;
}) {
  if (rows.length === 0) return <Empty title="Nothing matches that filter" />;

  return (
    <>
      <div className="delta-legend" style={{ marginBottom: 12 }}>
        <span>Price vs market:</span>
        <span>
          <span className="swatch" style={{ background: "var(--series-1)" }} />
          cheaper than market
        </span>
        <span>
          <span className="swatch" style={{ background: "var(--axis)" }} />
          within 10%
        </span>
        <span>
          <span
            className="swatch"
            style={{ background: "var(--status-critical)" }}
          />
          dearer than market
        </span>
      </div>
      <div className="tile-grid">
        {rows.map((row) => {
          return (
            <div className="shop-tile" key={row.itemKey}>
              <div className="shop-tile-head">
                {/* Every hashed item gets the in-game card on hover, identified
                  or not - an unnamed one is exactly where the invitation to
                  name it belongs. */}
                <ItemLabel
                  itemKey={row.itemKey}
                  material={row.material}
                  displayName={row.displayName}
                  size={34}
                  onLabel={() => onLabel(row.itemKey)}
                  sub={
                    <div className="shop-tile-sub">
                      {row.variantHash ? (
                        <span className="mono-key">{row.itemKey}</span>
                      ) : (
                        row.material
                      )}
                    </div>
                  }
                />
                <span className="shop-tile-actions">
                  <button
                    className="ghost"
                    title="Re-read this item from the live market"
                    aria-label={`Refresh ${row.displayName}`}
                    style={{ padding: "2px 6px" }}
                    disabled={refreshing != null}
                    onClick={() => onRefresh(row.material)}
                  >
                    {refreshing === row.material ? "..." : "↻"}
                  </button>
                  <button
                    className="ghost"
                    title="Hide this item from your shop view"
                    aria-label={`Hide ${row.displayName}`}
                    style={{ padding: "2px 6px" }}
                    disabled={refreshing != null}
                    onClick={() => onHide(row.itemKey)}
                  >
                    {"×"}
                  </button>
                </span>
              </div>

              {/* One row per chest. An owner may run several for the same item at
                different prices, and collapsing them hid stock they have. */}
              <div className="sign-table">
                <span className="head">Price</span>
                <span className="head r">vs market</span>
                <span className="head r">Stock</span>

                {row.signs.map((sign, index) => {
                  const delta =
                    row.marketPrice != null && row.marketPrice > 0
                      ? ((sign.unitPrice - row.marketPrice) / row.marketPrice) *
                        100
                      : null;
                  const empty = (sign.currentStock ?? 0) === 0;
                  return (
                    <Fragment key={sign.shopId}>
                      {index > 0 && <span className="sign-row-sep" />}
                      <span
                        className={`sign-price ${empty ? "sign-empty" : ""}`}
                      >
                        {batchPrice(sign.signPrice, sign.batchQty) ??
                          unitMoney(sign.unitPrice)}
                      </span>
                      <span className="num">
                        {delta == null ? (
                          <span className="muted">-</span>
                        ) : (
                          <Badge
                            tone={
                              delta > 10
                                ? "critical"
                                : delta < -10
                                  ? "warning"
                                  : "good"
                            }
                          >
                            {percent(delta)}
                          </Badge>
                        )}
                      </span>
                      <span className="num">
                        {empty ? (
                          <Badge tone="warning">empty</Badge>
                        ) : (
                          number(sign.currentStock ?? 0)
                        )}
                      </span>
                      <span className="sign-each">
                        <span className="sign-each-price">
                          {unitMoney(sign.unitPrice)} each
                          {/* Tax on what the sign charges, not on one unit of
                            it: a sign selling 64 for $9.99 owes $0.2498, and
                            the per-unit share is four hundredths of a cent.
                            Withheld from the proceeds, so the owner banks the
                            difference - see SALES_TAX_RATE for the statute. */}
                          <span className="sign-tax">
                            {" - Tax "}
                            {taxMoney(
                              salesTaxExact(sign.signPrice ?? sign.unitPrice),
                            )}
                          </span>
                        </span>
                        {sign.stockAt && (
                          <span className="sign-scanned">
                            updated {relativeTime(sign.stockAt)}
                          </span>
                        )}
                      </span>
                    </Fragment>
                  );
                })}
              </div>

              {/* The four figures the owner actually prices against. Low/high are
                percentiles of the cleaned sample, so a single troll listing at
                $1 or $1,000,000 no longer defines either end. */}
              {row.band ? (
                <div className="price-band">
                  <span className="head">Low</span>
                  <span className="head">Average</span>
                  <span className="head">High</span>
                  <span className="head rec">Suggested</span>
                  <span>{unitMoney(row.band.low)}</span>
                  <span>{unitMoney(row.band.average)}</span>
                  <span>{unitMoney(row.band.high)}</span>
                  <span className="rec" title={row.band.recommendedReason}>
                    {unitMoney(row.band.recommended)}
                  </span>
                  <span className="price-band-foot">
                    {number(row.band.sampleSize)} shops compared
                    {row.band.outliersRemoved > 0
                      ? ` - ${number(row.band.outliersRemoved)} outlier${row.band.outliersRemoved === 1 ? "" : "s"} dropped`
                      : ""}
                    {/* What actually traded, when the server knows. Kept out of
                      the band above because it is a different population - it
                      can legitimately sit outside what shops ask today. */}
                    {row.band.tradedAverage != null &&
                      ` - traded at ${unitMoney(row.band.tradedAverage)}`}
                  </span>
                </div>
              ) : (
                <div className="shop-tile-row">
                  <span className="k">Market</span>
                  <span className="v muted">no comparison</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function buildColumns(
  hasSalesData: boolean,
  onRefresh: (material: string) => void,
  onHide: (itemKey: string) => void,
  onLabel: (itemKey: string) => void,
  refreshing: string | null,
): Array<Column<Row>> {
  return [
    {
      key: "item",
      header: "Item",
      searchText: (row) =>
        `${row.title} ${row.displayName} ${row.material} ${row.variantLabel ?? ""}`,
      sortValue: (row) => row.title,
      render: (row) => (
        <ItemLabel
          itemKey={row.itemKey}
          material={row.material}
          displayName={row.displayName}
          onLabel={() => onLabel(row.itemKey)}
          sub={
            <span className="small muted">
              {row.variantHash ? (
                <span className="mono-key">{row.itemKey}</span>
              ) : (
                row.material
              )}
            </span>
          }
        />
      ),
    },
    {
      key: "sign",
      header: "Sign price",
      align: "num",
      sortValue: (row) => row.signPrice,
      render: (row) => (
        <>
          {row.signPrice == null ? "-" : money(row.signPrice)}
          {row.batchQty > 1 && (
            <div className="small muted">for {number(row.batchQty)}</div>
          )}
        </>
      ),
    },
    {
      key: "unit",
      header: "Per unit",
      align: "num",
      sortValue: (row) => row.yourPrice,
      render: (row) => unitMoney(row.yourPrice),
    },
    {
      key: "low",
      header: "Low",
      align: "num",
      sortValue: (row) => row.band?.low ?? null,
      render: (row) =>
        row.band ? unitMoney(row.band.low) : <span className="muted">-</span>,
    },
    {
      key: "average",
      header: "Average",
      align: "num",
      sortValue: (row) => row.band?.average ?? null,
      render: (row) =>
        row.band ? (
          <>
            {unitMoney(row.band.average)}
            <div className="small muted">
              {row.band.tradedAverage != null
                ? `traded at ${unitMoney(row.band.tradedAverage)}`
                : `${number(row.band.sampleSize)} shops`}
              {row.band.outliersRemoved > 0
                ? `, ${row.band.outliersRemoved} dropped`
                : ""}
            </div>
          </>
        ) : (
          <span className="muted">no comparison</span>
        ),
    },
    {
      key: "high",
      header: "High",
      align: "num",
      sortValue: (row) => row.band?.high ?? null,
      render: (row) =>
        row.band ? unitMoney(row.band.high) : <span className="muted">-</span>,
    },
    {
      key: "recommended",
      header: "Recommended",
      align: "num",
      sortValue: (row) => row.band?.recommended ?? null,
      render: (row) =>
        row.band ? (
          <strong title={row.band.recommendedReason}>
            {unitMoney(row.band.recommended)}
          </strong>
        ) : (
          <span className="muted">-</span>
        ),
    },
    {
      key: "delta",
      header: "vs market",
      render: (row) =>
        row.deltaPct == null ? (
          <span className="muted">-</span>
        ) : (
          <DeltaBar pct={row.deltaPct} />
        ),
    },
    {
      key: "deltaPct",
      header: "Gap",
      align: "num",
      sortValue: (row) => row.deltaPct,
      render: (row) =>
        row.deltaPct == null ? (
          <span className="muted">-</span>
        ) : (
          <strong>{percent(row.deltaPct)}</strong>
        ),
    },
    {
      key: "signs",
      header: "Signs",
      align: "num",
      sortValue: (row) => row.signs.length,
      render: (row) =>
        row.signs.length === 1 ? (
          <span className="muted">1</span>
        ) : (
          <span
            title={row.signs
              .map(
                (sign) =>
                  unitMoney(sign.unitPrice) +
                  " - " +
                  number(sign.currentStock ?? 0) +
                  " in stock",
              )
              .join(", ")}
          >
            <strong>{row.signs.length}</strong>
          </span>
        ),
    },
    {
      key: "stock",
      header: "Stock",
      align: "num",
      sortValue: (row) => row.currentStock,
      render: (row) =>
        row.currentStock === 0 ? (
          <Badge tone="warning">empty</Badge>
        ) : (
          number(row.currentStock)
        ),
    },
    {
      key: "updated",
      header: "Updated",
      align: "num",
      sortValue: (row) => row.stockAt,
      render: (row) => <Freshness stockAt={row.stockAt} />,
    },
    {
      key: "revenue",
      header: "30d revenue",
      align: "num",
      sortValue: (row) => row.revenue30d,
      render: (row) =>
        hasSalesData ? (
          moneyShort(row.revenue30d)
        ) : (
          <span className="muted">n/a</span>
        ),
    },
    {
      key: "actions",
      header: "",
      width: "76px",
      render: (row) => (
        <span style={{ display: "inline-flex", gap: 2 }}>
          <button
            className="ghost"
            title="Re-read this item from the live market"
            aria-label={`Refresh ${row.displayName}`}
            style={{ padding: "2px 6px" }}
            disabled={refreshing != null}
            onClick={() => onRefresh(row.material)}
          >
            {refreshing === row.material ? "..." : "↻"}
          </button>
          <button
            className="ghost"
            title="Hide this sign from your shop view"
            aria-label={`Hide ${row.displayName}`}
            style={{ padding: "2px 6px" }}
            disabled={refreshing != null}
            onClick={() => onHide(row.itemKey)}
          >
            {"×"}
          </button>
        </span>
      ),
    },
    {
      key: "where",
      header: "Where",
      searchText: (row) => `${row.world ?? ""} ${row.coords ?? ""}`,
      sortValue: (row) => row.world,
      render: (row) => (
        <span className="muted small">
          {row.world ?? "-"}
          {row.coords ? <div>{row.coords}</div> : null}
        </span>
      ),
    },
  ];
}

/**
 * How current the reading is.
 *
 * Reports the server's own timestamp, not when we fetched it - our copy being
 * fresh says nothing when the upstream snapshot is a fortnight old, and showing
 * our fetch time would imply a currency the data does not have.
 */
function Freshness({ stockAt }: { stockAt: string | null }) {
  if (!stockAt) return null;
  const ageHours = (Date.now() - new Date(stockAt).getTime()) / 3_600_000;
  return (
    <div
      className={`freshness ${ageHours > 48 ? "stale" : ""}`}
      title={`The server last checked this shop's stock at ${new Date(stockAt).toLocaleString()}`}
    >
      Updated {relativeTime(stockAt)}
    </div>
  );
}
